import React, { useEffect, useMemo, useState } from "react";
import { Form, Button, Row, Col, Alert } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import SlidePanel from "../../components/SlidePanel/SlidePanel";
import * as inventoryService from "../../services/inventoryService";
import { getVendorCurrency } from "../../utils/currency";

const UNIT_OPTIONS = ["pcs", "ml", "L", "kg", "metre", "set", "box", "unit"];

const schema = Yup.object({
  name: Yup.string().required("Item name is required"),
  sku: Yup.string().nullable(),
  category_id: Yup.string().required("Category is required"),
  unit_of_measure: Yup.string().required(),
  purchase_cost: Yup.number().min(0, "Must be 0 or more").required("Purchase cost is required"),
  selling_price: Yup.number().min(0, "Must be 0 or more").nullable(),
  reorder_level: Yup.number().min(0).required(),
  opening_stock: Yup.number().min(0),
});

/**
 * Create/edit drawer for an inventory item - role=3 only (enforced by the
 * caller/route). Uses SlidePanel (the established modern drawer pattern)
 * and Formik+Yup, matching the 20+ existing forms in this app.
 */
function ItemFormDrawer({ show, onHide, item, onSaved }) {
  const [categories, setCategories] = useState([]);
  const [markupPct, setMarkupPct] = useState(0);
  const [customAttrs, setCustomAttrs] = useState([]); // [{key, value}]
  const isEdit = !!item?.id;
  const currency = getVendorCurrency();

  useEffect(() => {
    if (!show) return;
    inventoryService.getCategories().then((res) => setCategories(res?.data || []));
    inventoryService.getSettings().then((res) => setMarkupPct(Number(res?.data?.default_markup_pct) || 0));
    const attrs = item?.custom_attributes
      ? Object.entries(
          typeof item.custom_attributes === "string" ? JSON.parse(item.custom_attributes) : item.custom_attributes
        ).map(([key, value]) => ({ key, value }))
      : [];
    setCustomAttrs(attrs.length ? attrs : [{ key: "", value: "" }]);
  }, [show, item]);

  const initialValues = useMemo(
    () => ({
      name: item?.name || "",
      sku: item?.sku || "",
      description: item?.description || "",
      category_id: item?.category_id || "",
      unit_of_measure: item?.unit_of_measure || "pcs",
      purchase_cost: item?.purchase_cost ?? "",
      selling_price: item?.selling_price ?? "",
      reorder_level: item?.reorder_level ?? 0,
      opening_stock: item?.opening_stock ?? 0,
      supplier: item?.supplier || "",
      location: item?.location || "",
      is_active: item?.is_active ?? 1,
    }),
    [item]
  );

  const buildCustomAttributes = () =>
    customAttrs.reduce((acc, { key, value }) => {
      if (key.trim()) acc[key.trim()] = value;
      return acc;
    }, {});

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const payload = { ...values, custom_attributes: buildCustomAttributes() };
      const res = isEdit
        ? await inventoryService.updateItem(item.id, payload)
        : await inventoryService.createItem(payload);
      if (res?.success === false) {
        toast.error(res.message || "Could not save item");
      } else {
        toast.success(isEdit ? "Item updated" : "Item created");
        onSaved?.();
        onHide();
      }
    } catch (err) {
      toast.error(err.message || "Could not save item");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SlidePanel show={show} onHide={onHide} title={isEdit ? "Edit Item" : "New Item"} width={520}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit: submitForm, isSubmitting }) => {
          const suggested =
            values.purchase_cost && !values.selling_price
              ? (Number(values.purchase_cost) * (1 + markupPct / 100)).toFixed(2)
              : null;

          return (
            <Form onSubmit={submitForm} id="item-form">
              <Form.Group className="mb-3" controlId="item-name">
                <Form.Label>Name *</Form.Label>
                <Form.Control
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.name && !!errors.name}
                />
                <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="item-sku">
                    <Form.Label>SKU</Form.Label>
                    <Form.Control
                      name="sku"
                      placeholder="Auto-generated if left blank"
                      value={values.sku}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="item-category">
                    <Form.Label>Category *</Form.Label>
                    <Form.Select
                      className="inventory-select"
                      name="category_id"
                      value={values.category_id}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.category_id && !!errors.category_id}
                    >
                      <option value="" disabled>Select category</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">{errors.category_id}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3" controlId="item-description">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="description"
                  value={values.description}
                  onChange={handleChange}
                />
              </Form.Group>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3" controlId="item-uom">
                    <Form.Label>Unit of measure</Form.Label>
                    <Form.Select className="inventory-select" name="unit_of_measure" value={values.unit_of_measure} onChange={handleChange}>
                      {UNIT_OPTIONS.map((u) => (
                        <option key={u} value={u}>
                          {u}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3" controlId="item-purchase-cost">
                    <Form.Label>Purchase cost * ({currency})</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      name="purchase_cost"
                      value={values.purchase_cost}
                      onChange={handleChange}
                      isInvalid={touched.purchase_cost && !!errors.purchase_cost}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3" controlId="item-selling-price">
                    <Form.Label>Selling price ({currency})</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      name="selling_price"
                      placeholder={suggested ? `Suggested: ${currency} ${suggested}` : ""}
                      value={values.selling_price}
                      onChange={handleChange}
                    />
                    {suggested && (
                      <Form.Text className="text-muted">
                        Suggested from cost + markup: {currency} {suggested} (always editable)
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="item-reorder-level">
                    <Form.Label>Reorder level</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      name="reorder_level"
                      value={values.reorder_level}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="item-opening-stock">
                    <Form.Label>Opening stock</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      name="opening_stock"
                      disabled={isEdit}
                      value={values.opening_stock}
                      onChange={handleChange}
                    />
                    {isEdit && (
                      <Form.Text className="text-muted">
                        Use Stock-In / Adjust to change quantity after creation.
                      </Form.Text>
                    )}
                    {!isEdit && Number(values.opening_stock) === 0 && (
                      <Alert variant="warning" className="mt-2 mb-0">
                        This item will be created with 0 stock (out of stock). You can proceed.
                      </Alert>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="item-supplier">
                    <Form.Label>Preferred supplier</Form.Label>
                    <Form.Control name="supplier" value={values.supplier} onChange={handleChange} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="item-location">
                    <Form.Label>Storage location</Form.Label>
                    <Form.Control name="location" value={values.location} onChange={handleChange} />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <Form.Label className="mb-0">Custom attributes</Form.Label>
                  <Button
                    size="sm"
                    variant="link"
                    onClick={() => setCustomAttrs([...customAttrs, { key: "", value: "" }])}
                  >
                    + Add attribute
                  </Button>
                </div>
                {customAttrs.map((attr, idx) => (
                  <Row key={idx} className="mb-2">
                    <Col xs={5}>
                      <Form.Control
                        placeholder="e.g. part_number"
                        value={attr.key}
                        onChange={(e) => {
                          const next = [...customAttrs];
                          next[idx].key = e.target.value;
                          setCustomAttrs(next);
                        }}
                      />
                    </Col>
                    <Col xs={6}>
                      <Form.Control
                        placeholder="value"
                        value={attr.value}
                        onChange={(e) => {
                          const next = [...customAttrs];
                          next[idx].value = e.target.value;
                          setCustomAttrs(next);
                        }}
                      />
                    </Col>
                    <Col xs={1} className="d-flex align-items-center">
                      <Button
                        size="sm"
                        variant="link"
                        className="text-danger p-0"
                        onClick={() => setCustomAttrs(customAttrs.filter((_, i) => i !== idx))}
                      >
                        ✕
                      </Button>
                    </Col>
                  </Row>
                ))}
              </Form.Group>

              {isEdit && (
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    label="Active"
                    name="is_active"
                    checked={!!Number(values.is_active)}
                    onChange={(e) => handleChange({ target: { name: "is_active", value: e.target.checked ? 1 : 0 } })}
                  />
                </Form.Group>
              )}

              <div className="d-flex justify-content-end gap-2 mt-4 pt-3" style={{ borderTop: "1px solid #e5e7eb" }}>
                <Button variant="outline-secondary" onClick={onHide} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  style={{ background: "var(--button-color)", border: "none", color: "#fff" }}
                >
                  {isSubmitting ? "Saving…" : "Save Item"}
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </SlidePanel>
  );
}

export default ItemFormDrawer;
